import type { FieldHook } from 'payload';

type FormatSlugOptions = {
	stripEdgeDashes?: boolean;
};

/**
 * Formats a slug. A slug provided by Payload can be a localized object in the case of, for example, a sitemap.
 * If the value is a string, it will be formatted as a slug.
 * If the value is an object, it will be formatted as a slug for each locale.
 * @param val The value to format.
 * @param prefix The prefix to add to the slug.
 * @param options Formatting options. Set `stripEdgeDashes` to remove leading and trailing dashes after normalization.
 * @returns The formatted slug.
 */
export const formatSlug = <T extends string | { [locale: string]: string }>(
	val: T,
	prefix = '',
	options?: FormatSlugOptions,
): T => {
	if (typeof val === 'string') {
		return formatSlugString(val, prefix, options) as T;
	}
	return formatSlugObject(val, prefix, options) as T;
};

const formatSlugString = (val: string, prefix = '', options?: FormatSlugOptions): string => {
	const slug = val
		.normalize('NFD') // split letters and diacritics (e.g., é -> e + ́)
		.replace(/[\u0300-\u036f]/g, '') // remove diacritical marks
		.replace(/[^\p{L}\p{N}\s/-]+/gu, '') // keep letters, numbers, spaces, dashes and forward slashes
		.replace(/[\s-]+/g, '-') // normalize sequences of space/dash into a single dash
		.toLowerCase();
	const normalizedSlug = options?.stripEdgeDashes ? slug.replace(/^-+|-+$/g, '') : slug;

	if (prefix && !normalizedSlug.startsWith(prefix)) {
		return `${prefix}${normalizedSlug}`;
	}

	return normalizedSlug;
};

const formatSlugObject = (
	val: { [locale: string]: string },
	prefix = '',
	options?: FormatSlugOptions,
): { [locale: string]: string } => {
	return Object.entries(val).reduce<{ [locale: string]: string }>((result, [locale, value]) => {
		result[locale] = formatSlugString(value, prefix, options);
		return result;
	}, {});
};

export const formatSlugHook =
	(fallback: string, prefix = ''): FieldHook =>
	({ data, operation, value }) => {
		if (typeof value === 'string') {
			return formatSlug(value, prefix, { stripEdgeDashes: true });
		}

		if (operation === 'create' || !data?.slug) {
			const fallbackData = data?.[fallback];

			if (fallbackData && typeof fallbackData === 'string') {
				return formatSlug(fallbackData, prefix, { stripEdgeDashes: true });
			}
		}

		return value;
	};
